import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Paper,
} from "@material-ui/core";
import { ThumbUp, ThumbDown } from "@material-ui/icons";
import MarkdownWrapper from "../../components/MarkdownWrapper";
import HelpLayout from "../../components/HelpCenter/HelpLayout";
import { useHelpArticle } from "../../hooks/useHelp";
import { createHelpArticleFeedback } from "../../services/helpApi";
import { i18n } from "../../translate/i18n";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(8),
  },
  header: {
    marginBottom: theme.spacing(5),
    paddingBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontSize: "2.5rem",
    lineHeight: 1.2,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    width: "fit-content",
  },
  content: {
    marginBottom: theme.spacing(6),
    fontSize: "1rem",
    lineHeight: 1.8,
    color: theme.palette.text.primary,
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
      fontWeight: 600,
      color: theme.palette.primary.main,
      lineHeight: 1.3,
    },
    "& h1": {
      fontSize: "2.25rem",
      borderBottom: `2px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(1),
    },
    "& h2": {
      fontSize: "1.875rem",
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(0.5),
    },
    "& h3": {
      fontSize: "1.5rem",
    },
    "& h4": {
      fontSize: "1.25rem",
    },
    "& p": {
      marginBottom: theme.spacing(2.5),
      lineHeight: 1.8,
    },
    "& ul, & ol": {
      marginBottom: theme.spacing(3),
      paddingLeft: theme.spacing(5),
      "& li": {
        marginBottom: theme.spacing(1.5),
        lineHeight: 1.7,
      },
    },
    "& strong, & b": {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    "& em, & i": {
      fontStyle: "italic",
    },
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      borderBottom: `1px dotted ${theme.palette.primary.main}`,
      transition: "all 0.2s ease",
      "&:hover": {
        borderBottom: `1px solid ${theme.palette.primary.main}`,
        opacity: 0.8,
      },
    },
    "& code": {
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
      color: theme.palette.secondary.main,
      padding: "3px 8px",
      borderRadius: 4,
      fontSize: "0.875rem",
      fontFamily: "'Courier New', Courier, monospace",
      border: `1px solid ${theme.palette.divider}`,
    },
    "& pre": {
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
      color: theme.palette.text.primary,
      padding: theme.spacing(2.5),
      borderRadius: theme.spacing(1),
      overflow: "auto",
      marginBottom: theme.spacing(3),
      border: `1px solid ${theme.palette.divider}`,
      "& code": {
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
      },
    },
    "& blockquote": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      marginLeft: 0,
      marginRight: 0,
      marginBottom: theme.spacing(3),
      color: theme.palette.text.secondary,
      fontStyle: "italic",
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
      borderRadius: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px 0`,
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: theme.spacing(1),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    "& hr": {
      border: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
  },
  feedbackSection: {
    marginTop: theme.spacing(8),
    padding: theme.spacing(4),
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
    borderRadius: theme.spacing(2),
    textAlign: "center",
    border: `1px solid ${theme.palette.divider}`,
  },
  feedbackButtons: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "center",
    marginTop: theme.spacing(3),
  },
  feedbackButton: {
    minWidth: 140,
    padding: theme.spacing(1.5, 3),
  },
  stats: {
    marginTop: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
}));

const HelpArticlePage = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const { article, loading } = useHelpArticle(slug);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const getCurrentLang = () => {
    const lang = i18n.language || "pt";
    return lang.toLowerCase().substring(0, 2);
  };

  const getArticleContent = () => {
    if (!article) return {};
    const lang = getCurrentLang();
    return {
      title: lang === "en" ? article.titleEn : lang === "es" ? article.titleEs : article.titlePt,
      description: lang === "en" ? article.descriptionEn : lang === "es" ? article.descriptionEs : article.descriptionPt,
      content: lang === "en" ? article.contentEn : lang === "es" ? article.contentEs : article.contentPt,
    };
  };

  const handleFeedback = async (helpful) => {
    try {
      await createHelpArticleFeedback(article.id, { helpful });
      setFeedbackSent(true);
      toastUtils.success(i18n.t("helpCenter.thanksFeedback"));
    } catch (err) {
      console.error("Erro ao enviar feedback:", err);
    }
  };

  if (loading) {
    return (
      <HelpLayout>
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      </HelpLayout>
    );
  }

  if (!article) {
    return (
      <HelpLayout>
        <Box className={classes.loading}>
          <Typography variant="h5">
            {i18n.t("helpCenter.articleNotFound")}
          </Typography>
        </Box>
      </HelpLayout>
    );
  }

  const content = getArticleContent();
  const breadcrumbs = [
    {
      label: article.category ? (getCurrentLang() === "en" ? article.category.nameEn : getCurrentLang() === "es" ? article.category.nameEs : article.category.namePt) : "",
      path: article.category ? `/help/${article.category.slug}` : "",
    },
    {
      label: content.title,
    },
  ];

  return (
    <HelpLayout breadcrumbs={breadcrumbs}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {content.title}
        </Typography>
        {content.description && (
          <Typography variant="subtitle1" color="textSecondary" paragraph>
            {content.description}
          </Typography>
        )}
        <Box className={classes.meta}>
          <span>👁 {article.views} {i18n.t("helpCenter.views")}</span>
          <span>•</span>
          <span>📅 {new Date(article.updatedAt).toLocaleDateString()}</span>
        </Box>
      </Box>

      <Divider />

      <Box className={classes.content} mt={4}>
        <MarkdownWrapper>{content.content}</MarkdownWrapper>
      </Box>

      <Divider />

      <Paper className={classes.feedbackSection} elevation={0}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("helpCenter.wasHelpful")}
        </Typography>
        {!feedbackSent ? (
          <Box className={classes.feedbackButtons}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ThumbUp />}
              onClick={() => handleFeedback(true)}
              className={classes.feedbackButton}
            >
              {i18n.t("helpCenter.yes")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ThumbDown />}
              onClick={() => handleFeedback(false)}
              className={classes.feedbackButton}
            >
              {i18n.t("helpCenter.no")}
            </Button>
          </Box>
        ) : (
          <Typography color="primary" variant="body1">
            ✓ {i18n.t("helpCenter.thanksFeedback")}
          </Typography>
        )}
        <Typography className={classes.stats}>
          {article.helpful + article.notHelpful > 0 && (
            <>
              {article.helpful} {i18n.t("helpCenter.helpful")} •{" "}
              {article.notHelpful} {i18n.t("helpCenter.notHelpful")}
            </>
          )}
        </Typography>
      </Paper>
    </HelpLayout>
  );
};

export default HelpArticlePage;
